// 导入所需的模块和类型
import UAParser, {IResult} from 'ua-parser-js';
import Util from "@/Master/Util";

interface NavigatorWithConnection extends Navigator {
    connection: any;
}
/**
 * UtilBrowser 类用于解析和获取浏览器相关的信息
 */
export default class UtilBrowser {
    // 静态属性定义
    static tag: string = `[UtilBrowser]`;
    static uap: UAParser | null = null; // UAParser 实例
    static res: IResult | null = null; // UAParser 解析结果

    /**
     * 初始化方法，创建 UAParser 实例并获取解析结果
     */
    static Init(): void {
        // 创建 UAParser 实例
        this.uap = new UAParser();
        this.res = this.uap.getResult();

        if (Util.isLog) {
            console.log(this.tag, `UAParser => ${JSON.stringify(this.res)}`);
        }
    }

    /**
     * 获取浏览器的语言设置
     */
    static get getLanguage(): string {
        return (navigator.language || (navigator as any).userLanguage).toLowerCase();
    }

    /**
     * 获取系统所在的国家/地区代码
     */
    static get getSystemCountry(): string {
        try {
            const language = navigator.language || (navigator as any).userLanguage;
            const parts = language.split(/[-_]/);
            if (parts[1]) {
                return parts[1].toUpperCase();
            }else{
                if (parts[0]) {
                    return parts[0];
                }
            }
            return '';
        } catch (e) {
            // console.error(`${this.tag} 获取系统国家/地区代码失败:`, e);
        }
        return '';
    }

    /**
     * 获取浏览器的名称
     */
    static get getBrowserName(): string {
        return this.res?.browser.name || 'Unknown';
    }

    /**
     * 获取浏览器的版本
     */
    static get getBrowserVersion(): string {
        return this.res?.browser.version || 'Unknown';
    }

    /**
     * 获取操作系统的名称
     */
    static get getOsName(): string {
        return this.res?.os.name || 'Unknown';
    }

    /**
     * 获取操作系统的版本
     */
    static get getOsVersion(): string {
        return this.res?.os.version || 'Unknown';
    }

    /**
     * 判断当前是否为桌面环境
     */
    static get isDesktop(): boolean {
        const userAgent = navigator.userAgent.toLowerCase();
        const desktopPlatforms = ['win', 'mac', 'linux'];

        const isDesktopUA = desktopPlatforms.some(platform => userAgent.includes(platform));
        const isWideViewport = window.innerWidth >= 1024;

        return isDesktopUA && isWideViewport;
    }

    /**
     * 判断是否在 Facebook 旗下的应用中打开（如 Facebook App, Instagram, WhatsApp 等）
     */
    static get isFacebookApp(): boolean {
        // @ts-ignore
        const userAgent = navigator.userAgent || navigator.vendor || (window.opera as string);
        return /FBAN|FBAV|Instagram|WhatsApp|OculusBrowser|Messenger|Workplace/.test(userAgent);
    }

    /**
     * 判断是否为 Google Chrome 浏览器（排除其他基于 Chromium 的浏览器）
     */
    static get isGoogleChrome(): boolean {
        const userAgent = navigator.userAgent.toLowerCase();

        if (userAgent.includes("chrome")) {
            if (userAgent.includes("edg")) {
                return false; // 排除 Microsoft Edge
            }
            if (userAgent.includes("opr") || userAgent.includes("opera")) {
                return false; // 排除 Opera
            }
            if (userAgent.includes("chromium")) {
                return true; // 检测 Chromium
            }
            if (userAgent.includes("mobile safari") && userAgent.includes("crios")) {
                return true; // 检测 Chrome Mobile
            }
            if (userAgent.includes("canary")) {
                return true; // 检测 Chrome Canary
            }
            if (userAgent.includes("chrome") && userAgent.includes("beta")) {
                return true; // 检测 Chrome Beta
            }
            if (userAgent.includes("chrome") && userAgent.includes("dev")) {
                return true; // 检测 Chrome Dev
            }
            if (userAgent.includes("wv")) {
                return true; // 检测 Chrome WebView
            }
            return true; // 检测 Google Chrome (包括 Chrome OS)
        }
        return false; // 未匹配到任何 Google 浏览器
    }

    /**
     * 在 Google Chrome 中打开指定的 URL
     * @param url 目标 URL
     * @param addParam 是否添加额外的查询参数
     */
    static openInChrome(url: string, addParam: boolean = false): void {
        try {
            let targetUrl = new URL(url);

            // 如果需要添加查询参数
            if (addParam) {
                targetUrl.searchParams.set("is_open_chrome", "1");
            }

            // 构建 Intent URL 以在 Chrome 中打开
            const intentUrl = `intent://${targetUrl.href.replace(/(https|http):\/\//, "")}#Intent;scheme=https;action=android.intent.action.VIEW;component=com.android.chrome;package=com.android.chrome;end`;
            location.href = intentUrl;
        } catch (e) {
            console.error(`${this.tag} 打开 Chrome 时出错:`, e);
        }
    }

    /**
     * 判断当前是否为 Safari 浏览器
     */
    static get isSafari(): boolean {
        const ua = navigator.userAgent;
        const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(ua);
        const isSafariGlobal = !!(window as any).safari;

        return isSafariBrowser && isSafariGlobal;
    }


    /**
     * 取用户设备的内存信息
     */
    static get deviceMemory():string{
        if ('deviceMemory' in navigator) {
            const deviceMemory =  (navigator as any).deviceMemory as number;
            return `${deviceMemory} GB`;
        } else {
            return `0GB`;
        }
    }

    static get getNetworkType(): string {
        const navigatorWithConnection = navigator as NavigatorWithConnection;
        if (navigatorWithConnection.connection) {
            const connection = navigatorWithConnection.connection;
            // console.log(`连接类型: ${connection.type}`);
            // console.log(`有效连接类型: ${connection.effectiveType}`);
            // console.log(`下行速度: ${connection.downlink} Mbps`);
            // console.log(`往返时间: ${connection.rtt} ms`);
            // console.log(`数据节省模式: ${connection.saveData ? '启用' : '未启用'}`);
            return `${connection?.type}`;
        } else {
            // console.log('网络信息API 不受支持。');
            return '0';
        }
    }


}

// 如果处于测试模式，则将 UtilBrowser 挂载到 window 对象上，方便调试
// window.utilBrowser = UtilBrowser;
